import React from 'react'
import { useSelector } from 'react-redux'
import userSelectors from '../../../../store/selectors/user.selectors'
import assignmentSelectors from '../../../../store/selectors/assignment.selectors'
import {
    Perm,
    PERMISSION_LEVEL_TO_PERMISSION_SCOPES,
    PermissionLevels,
    State,
    STATE_ERROR_MESSAGES,
} from '../../../../constants/assignment-permissions'
import versionSelectors from '../../../../store/selectors/version.selectors'
import { ASSIGNMENT_TYPES } from '../../../../constants/assignment-types'
import PropTypes from 'prop-types'
import { useFetchAssignmentsQuery } from '../../../../store/apis/assignment.api'
import { fetchAssignmentsSelectors } from '../../../../store/apis/utils/selectors/assignment.api.selectors'
import { checkIfAssignmentIsProcessing } from '../../../../utils/assignment.helper'
import Routes from '../../../../constants/routes'
import { useVersionId } from '../../../../utils/parseURL.helper'
import { useIsCurrentRoute } from '../../../../hooks/useIsCurrentRoute'
import useAssignmentFilters from '../../../../hooks/useAssignmentFilters'
import useSubmissionGroups from '../../sections/submissions/submission-groups/useSubmissionGroups'

const usePermissionLevel = token => {
    const permissionLevelFromStore = useSelector(assignmentSelectors.permissionLevel)
    const queryArgs = useAssignmentFilters()

    const { data: permissionLevelByTokenFromApi } = useFetchAssignmentsQuery(queryArgs, {
        skip: !token,
        selectFromResult: result => fetchAssignmentsSelectors.selectPermissionLevelByToken({ token }, result),
    })

    const isAssignmentsRoute = useIsCurrentRoute(Routes.ASSIGNMENTS, true)
    const isHomeRoute = useIsCurrentRoute(Routes.HOME, true)
    const isAssignmentsPage = isAssignmentsRoute || isHomeRoute
    if (!isAssignmentsPage) return permissionLevelFromStore

    return permissionLevelByTokenFromApi || permissionLevelFromStore
}

const useProcessingStatus = token => {
    const processingStatusFromStore = useSelector(assignmentSelectors.processingStatus)
    const queryArgs = useAssignmentFilters()

    const { data: processingStatusByTokenFromApi } = useFetchAssignmentsQuery(queryArgs, {
        skip: !token,
        selectFromResult: result => fetchAssignmentsSelectors.selectProcessingStatusByToken({ token }, result),
    })

    //  remove this once we have a proper way to get the processing status from the backend
    const isAssignmentsRoute = useIsCurrentRoute(Routes.ASSIGNMENTS, true)
    const isHomeRoute = useIsCurrentRoute(Routes.HOME, true)
    const isAssignmentsPage = isAssignmentsRoute || isHomeRoute
    if (!isAssignmentsPage) return processingStatusFromStore
    //  remove this once we have a proper way to get the processing status from the backend

    return processingStatusByTokenFromApi || processingStatusFromStore
}

const useType = token => {
    const typeFromStore = useSelector(assignmentSelectors.type)
    const queryArgs = useAssignmentFilters()

    const { data: typeByTokenFromApi } = useFetchAssignmentsQuery(queryArgs, {
        skip: !token,
        selectFromResult: result => fetchAssignmentsSelectors.selectTypeByToken({ token }, result),
    })

    //  remove this once we have a proper way to get the type from the backend
    const isAssignmentsRoute = useIsCurrentRoute(Routes.ASSIGNMENTS, true)
    const isHomeRoute = useIsCurrentRoute(Routes.HOME, true)
    const isAssignmentsPage = isAssignmentsRoute || isHomeRoute
    if (!isAssignmentsPage) return typeFromStore
    //  remove this once we have a proper way to get the type from the backend

    return typeByTokenFromApi || typeFromStore
}

const useIsVersionLatest = () => {
    const isAssignmentsRoute = useIsCurrentRoute(Routes.ASSIGNMENTS, true)
    const isHomeRoute = useIsCurrentRoute(Routes.HOME, true)
    const isAssignmentsPage = isAssignmentsRoute || isHomeRoute
    let versionId = useVersionId()
    versionId = versionId ? parseInt(versionId) : null
    const isVersionLatest = useSelector(versionSelectors.isLatestById(versionId))
    return isAssignmentsPage ? false : isVersionLatest // on assignments and shared assignments pages we can't know if the version is latest, we see the default one
}

const useIsVersionPublished = token => {
    let versionId = useVersionId()
    versionId = versionId ? parseInt(versionId) : null

    const isAssignmentsRoute = useIsCurrentRoute(Routes.ASSIGNMENTS, true)
    const isHomeRoute = useIsCurrentRoute(Routes.HOME, true)
    const isAssignmentsPage = isAssignmentsRoute || isHomeRoute
    const isVersionPublishedFromStore = useSelector(versionSelectors.isPublishedById(versionId))
    const queryArgs = useAssignmentFilters()

    const { data: isVersionPublishedByTokenFromApi } = useFetchAssignmentsQuery(queryArgs, {
        skip: !token || !isAssignmentsPage,
        selectFromResult: result => fetchAssignmentsSelectors.selectIsVersionPublishedByToken({ token }, result),
    })

    //  remove this once we have a proper way to get the is version published from the backend
    if (!isAssignmentsPage) return isVersionPublishedFromStore
    //  remove this once we have a proper way to get the is version published from the backend

    return isVersionPublishedByTokenFromApi ?? isVersionPublishedFromStore
}

export const useAssignmentAccess = (rules = [], token, assigneeId) => {
    const userId = useSelector(userSelectors.id)
    const isVersionLatest = useIsVersionLatest()
    const isVersionPublished = useIsVersionPublished(token)
    const type = useType(token)
    const processingStatus = useProcessingStatus(token)
    
    // Skip submission fetching when not on an assignment page (e.g., on AssignmentsPage/Home)
    const isAssignmentsRoute = useIsCurrentRoute(Routes.ASSIGNMENTS, true)
    const isHomeRoute = useIsCurrentRoute(Routes.HOME, true)
    const skipSubmissionFetch = isAssignmentsRoute || isHomeRoute
    const { totalSubmissions } = useSubmissionGroups({ applyFilter: false, skip: skipSubmissionFetch })

    const permissionLevel = usePermissionLevel(token)
    const isOwner = permissionLevel === PermissionLevels.OWNER

    let userPermissions = permissionLevel
        ? [...(PERMISSION_LEVEL_TO_PERMISSION_SCOPES[permissionLevel] || [])]
        : isOwner
          ? Object.values(Perm)
          : []

    /**
     * Controls assignment-based access using a three-state system with granular permissions:
     *
     * assigneeId === undefined:
     *   - Open access (not assignment-specific)
     *   - All users with basic permissions can access
     *
     * assigneeId === null:
     *   - Unassigned items (awaiting assignment)
     *   - Accessible by users with CAN_EDIT_UNASSIGNED permission
     *
     * assigneeId === number:
     *   - Assigned to specific user
     *   - Accessible by: assignee, users with CAN_EDIT_ASSIGNED_TO_OTHERS permission
     *
     * This replaces the previous owner-only system with role-based granular permissions.
     */
    const canEditAssignedEntity =
        assigneeId === undefined ||
        assigneeId === userId ||
        (assigneeId === null && userPermissions.includes(Perm.CAN_EDIT_UNASSIGNED)) ||
        (typeof assigneeId === 'number' && userPermissions.includes(Perm.CAN_EDIT_ASSIGNED_TO_OTHERS))

    const isMBA = type === ASSIGNMENT_TYPES.MODEL_BOUND_ASSIGNMENT.value
    const hasSubmissions = Boolean(totalSubmissions)

    const stateChecks = {
        [State.NOT_MBA]: !isMBA,
        [State.LATEST]: isMBA || isVersionLatest,
        [State.UNPUBLISHED]: isMBA || !isVersionPublished,
        [State.NOT_PROCESSING]: !checkIfAssignmentIsProcessing(processingStatus),
        [State.NO_SUBMISSIONS]: !hasSubmissions,
    }

    for (const rule of rules) {
        if (Object.values(Perm).includes(rule)) {
            if (!userPermissions.includes(rule)) {
                return {
                    hasAccess: false,
                    failedRule: rule,
                    errorMessage: `Missing ${rule.replace('can-', '').replace('-', ' ')} permission`,
                }
            }
        } else if (Object.values(State).includes(rule)) {
            if (!stateChecks[rule]) {
                return {
                    hasAccess: false,
                    failedRule: rule,
                    errorMessage: STATE_ERROR_MESSAGES[rule] || `State condition failed: ${rule}`,
                }
            }
        }
    }

    if (!canEditAssignedEntity) {
        return {
            hasAccess: false,
            failedRule: 'assignment_access',
            errorMessage: 'Cannot edit this assigned item',
        }
    }

    return {
        hasAccess: true,
        failedRule: null,
        errorMessage: null,
    }
}

const Access = ({
    children,
    fallback = null,
    rules = [],
    token = null,
    assigneeId = undefined,
    forceFallback = false,
    disableWhenNoAccess = false,
    ...rest
}) => {
    const { hasAccess, errorMessage } = useAssignmentAccess(rules, token, assigneeId)
    
    if (!hasAccess || forceFallback) {
        if (fallback) {
            return fallback
        }
        
        if (!disableWhenNoAccess) {
            return null
        }
        
        if (React.isValidElement(children) && errorMessage) {
            return React.cloneElement(children, {
                disabled: true,
                title: errorMessage,
            })
        }
        
        return null
    }

    return React.Children.map(children, child => {
        if (React.isValidElement(child)) {
            const oldOnChange = child.props.onChange
            const newOnChange = rest.onChange
            const mergedOnChange = event => {
                oldOnChange && oldOnChange(event)
                newOnChange && newOnChange(event)
            }
            return React.cloneElement(child, { ...rest, onChange: mergedOnChange })
        }
        return child
    })
}

Access.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    fallback: PropTypes.element,
    rules: PropTypes.arrayOf(PropTypes.oneOf([...Object.values(Perm), ...Object.values(State)])),
    token: PropTypes.string, // if passed, the permissions will be checked based on the assignments list from useFetchAssignmentsQuery
    /**
     * Controls assignment-based access using a three-state system with granular permissions:
     *
     * assigneeId === undefined:
     *   - Open access (not assignment-specific)
     *   - All users with basic permissions can access
     *
     * assigneeId === null:
     *   - Unassigned items (awaiting assignment)
     *   - Accessible by users with CAN_EDIT_UNASSIGNED permission
     *
     * assigneeId === number:
     *   - Assigned to specific user
     *   - Accessible by: assignee, users with CAN_EDIT_ASSIGNED_TO_OTHERS permission
     *
     * This replaces the previous owner-only system with role-based granular permissions.
     */
    assigneeId: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null, undefined])]),
    forceFallback: PropTypes.bool, // if true, forces the fallback behavior even if user has access
    showDisabledWhenNoAccess: PropTypes.bool, // if true, shows the element disabled with tooltip when no access; if false (default), hides element completely
}

export default Access
